import React, { useState, useEffect, useCallback, useRef } from 'react';
// ReactDOM removed - not needed for scroll tracking approach
import { useConversation } from '@elevenlabs/react';
import { sectionMappings } from '../config/sectionMappings';
import { translations } from '../config/translations';
import QRCode from 'qrcode';
import { fetchArticles, searchArticles } from '../utils/articleSearch';
import { splitLinks } from '../utils/linkify';
import { stripVoiceCues } from '../utils/voiceCues';
import { track } from '../utils/analytics';
import './VoiceAgent.css';

// Lenny the Lime, served from this widget's own Netlify site (an absolute URL, because the
// bundle's relative asset paths would resolve against the host page). Also used by the lazy
// loader's placeholder and by foodfacts.org's inline "Ask Lenny" cards.
const LENNY_AVATAR = 'https://foodfacts-voice-agent.netlify.app/lenny-avatar.png';

// Available languages
const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'zh', name: 'Chinese', flag: '🇨🇳' },
  { code: 'de', name: 'German', flag: '🇩🇪' },
  { code: 'hi', name: 'Hindi', flag: '🇮🇳' },
  { code: 'es', name: 'Spanish', flag: '🇪🇸' },
];

// Diverse collection of suggested questions that rotate
const SUGGESTED_QUESTIONS = [
  // Health & Nutrition Myths
  "Is milk really full of pus?",
  "Does freezing bread actually improve blood sugar?",
  "Are plastic containers dangerous for food storage?",
  "Can folic acid really harm my child's behavior?",
  "Do croissants actually raise cancer risk?",
  "What's really in plant-based meat?",
  
  // Food Policy & Systems
  "How much do EU subsidies favor beef over legumes?",
  "Why did the Netherlands cut meat recommendations?",
  "What does 'free range' actually mean?",
  "Is humane slaughter really a thing?",
  "How is fish farming affecting wild fish populations?",
  
  // Food Industry Practices
  "What's the weird history behind MSG's bad reputation?",
  "How are chickens really slaughtered in the UK?",
  "What does 'grass-fed' really mean for cows?",
  "Why are people switching away from plant-based meat?",
  
  // Environmental & Climate
  "What's the environmental cost of different foods?",
  "How does food production impact climate change?",
  "Which foods have the biggest carbon footprint?",
  
  // Media Literacy & Misinformation
  "How do I spot nutrition misinformation online?",
  "Why do health influencers spread false claims?",
  "What makes a food claim actually credible?",
  "How accurate is health advice on social media?",
  
  // General Exploration
  "What food myths do you debunk most?",
  "Tell me about the latest food research",
  "What should I know about food labels?"
];

// Function to get random questions without repeats
function getRandomQuestions(count = 3) {
  const shuffled = [...SUGGESTED_QUESTIONS].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

// ---------------------------------------------------------------------------
// WhatsApp channel. Lenny also answers on WhatsApp through the ElevenLabs Agents
// WhatsApp integration: people just message the business number (text or voice
// note) and the same agent replies. Phones get a wa.me deep link; desktops get a
// QR code to scan (plus the link for WhatsApp Web/desktop). Override the number
// from the host page with window.FoodFactsVoiceAgent.whatsapp = '4475...'.
// ---------------------------------------------------------------------------
const WHATSAPP_DEFAULT_NUMBER = '447356131717';
const WHATSAPP_PREFILL = 'Hi Lenny 👋 I have a food question:';
const WHATSAPP_ICON = 'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z';

function getWhatsAppNumber() {
  try {
    const n = window.FoodFactsVoiceAgent && window.FoodFactsVoiceAgent.whatsapp;
    if (n) return String(n).replace(/[^0-9]/g, '');
  } catch (e) { /* ignore */ }
  return WHATSAPP_DEFAULT_NUMBER;
}
function getWhatsAppLink() {
  return `https://wa.me/${getWhatsAppNumber()}?text=${encodeURIComponent(WHATSAPP_PREFILL)}`;
}
function formatWhatsAppNumber() {
  const n = getWhatsAppNumber();
  return n.startsWith('44') ? `+44 ${n.slice(2, 6)} ${n.slice(6)}` : `+${n}`;
}
function isMobileDevice() {
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) || (navigator.maxTouchPoints > 1 && window.innerWidth < 900);
}
const WhatsAppGlyph = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d={WHATSAPP_ICON} /></svg>
);

// ---------------------------------------------------------------------------
// Stable per-visitor identity for ElevenLabs.
// We pass a `user_id` dynamic variable at conversation start so the agent can
// recognise the same visitor across sessions. NOTE: the ElevenLabs agent must
// reference {{user_id}} (in its prompt/tools) for this to create real memory;
// this only guarantees the site always identifies the same visitor.
// ---------------------------------------------------------------------------
const USER_ID_STORAGE_KEY = 'ff_user_id';

// Returns a logged-in id if the host page exposes one anywhere in context,
// otherwise creates and reuses a persistent anonymous id for this browser.
function getOrCreateUserId() {
  // 1) Prefer a real logged-in identity if the site exposes one.
  if (typeof window !== 'undefined') {
    const cfg = window.FoodFactsVoiceAgent || {};
    const loggedIn =
      window.FF_USER_ID || cfg.userId || (window.foodfactsUser && window.foodfactsUser.id);
    if (loggedIn) return String(loggedIn);
  }

  // 2) Otherwise fall back to a persistent anonymous id in this browser.
  try {
    let userId = localStorage.getItem(USER_ID_STORAGE_KEY);
    if (!userId) {
      const uuid =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      userId = `user_${uuid}`;
      localStorage.setItem(USER_ID_STORAGE_KEY, userId);
    }
    return userId;
  } catch (e) {
    // localStorage blocked (private mode / restricted iframe) — session-only id
    // so the conversation still starts.
    return `user_anon_${Date.now()}`;
  }
}

// Merge user_id (and any safe, already-available profile vars) into an existing
// dynamic-variables object WITHOUT overwriting variables the widget may already
// send. ElevenLabs reads these at conversation initiation.
// ---------------------------------------------------------------------------
// Visitor memory. ElevenLabs' native user memory is not available on this
// workspace, so the site keeps a compact memory itself: the visitor's first
// name plus the last few exchanges, stored in localStorage (and mirrored to the
// member's Memberstack JSON by the site script via the 'ff:lenny-memory' event).
// It is sent to the agent at session start as the {{visitor_memory}} variable.
// ---------------------------------------------------------------------------
const MEMORY_KEY = 'ff_lenny_memory';
const MEMORY_MAX_SESSIONS = 3;
const MEMORY_MAX_LINES = 10;
const MEMORY_MAX_LINE_CHARS = 220;
const MEMORY_MAX_CHARS = 1400;

function loadMemory() {
  let local = null;
  try { local = JSON.parse(localStorage.getItem(MEMORY_KEY) || 'null'); } catch (e) { local = null; }
  let remote = null;
  try { remote = window.FoodFactsVoiceAgent && window.FoodFactsVoiceAgent.memory; } catch (e) { remote = null; }
  if (remote && typeof remote !== 'object') remote = null;
  const newer = (a, b) => (Date.parse((b && b.updatedAt) || 0) > Date.parse((a && a.updatedAt) || 0) ? b : a);
  const mem = !local ? remote : !remote ? local : newer(local, remote);
  return mem && Array.isArray(mem.sessions) ? mem : { v: 1, name: '', updatedAt: null, sessions: [] };
}
function saveMemory(mem) {
  mem.updatedAt = new Date().toISOString();
  try { localStorage.setItem(MEMORY_KEY, JSON.stringify(mem)); } catch (e) { /* ignore */ }
  try { window.dispatchEvent(new CustomEvent('ff:lenny-memory', { detail: mem })); } catch (e) { /* ignore */ }
}
function extractName(text) {
  const m = /\b(?:my name is|my name's|i am|i'm|im|call me|it's|this is)\s+([A-Za-z][a-zA-Z'-]{1,20})\b/i.exec(text || '');
  if (!m) return '';
  const n = m[1];
  const stop = /^(not|just|sure|fine|good|ok|okay|here|new|back|so|really|very|also|interested|curious|wondering|looking|trying|asking|going|happy|sorry|glad|afraid|a|an|the|from|in|on|at|to|about|still|only|actually|pretty|quite|super|kind|sort)$/i;
  return stop.test(n) ? '' : n.charAt(0).toUpperCase() + n.slice(1);
}
function recordMemoryLine(sessionRef, role, text) {
  if (role === 'ai' && /^Hi, I'm Lenny from foodfacts\.org/i.test(text || '')) return; // skip the canned greeting
  if (!sessionRef.current) sessionRef.current = { at: new Date().toISOString(), lines: [] };
  const sess = sessionRef.current;
  const line = (role === 'user' ? 'User: ' : 'Lenny: ') + String(text).replace(/\s+/g, ' ').trim().slice(0, MEMORY_MAX_LINE_CHARS);
  if (sess.lines.length && sess.lines[sess.lines.length - 1] === line) return;
  sess.lines.push(line);
  if (sess.lines.length > MEMORY_MAX_LINES) sess.lines = sess.lines.slice(-MEMORY_MAX_LINES);
  const mem = loadMemory();
  const idx = mem.sessions.findIndex((x) => x.at === sess.at);
  if (idx >= 0) mem.sessions[idx] = { at: sess.at, lines: sess.lines.slice() };
  else mem.sessions.unshift({ at: sess.at, lines: sess.lines.slice() });
  mem.sessions = mem.sessions.slice(0, MEMORY_MAX_SESSIONS);
  if (role === 'user') { const n = extractName(text); if (n) mem.name = n; }
  saveMemory(mem);
}
function formatMemory(mem) {
  if (!mem || !mem.sessions || !mem.sessions.length) return '';
  const parts = [];
  if (mem.name) parts.push(`Visitor's first name: ${mem.name}.`);
  parts.push('Previous conversations, most recent first:');
  for (const sess of mem.sessions) {
    let when = '';
    try { when = new Date(sess.at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); } catch (e) { when = ''; }
    parts.push(`Conversation on ${when}:\n${sess.lines.join('\n')}`);
  }
  let out = parts.join('\n\n');
  if (out.length > MEMORY_MAX_CHARS) out = out.slice(0, MEMORY_MAX_CHARS) + '…';
  return out;
}

function buildDynamicVariables(existing = {}) {
  const vars = { ...existing };
  vars.user_id = getOrCreateUserId();

  // Optional extras — only if the host already exposes them reliably.
  if (typeof window !== 'undefined') {
    const cfg = window.FoodFactsVoiceAgent || {};
    if (cfg.userName) vars.user_name = String(cfg.userName);
    if (cfg.accountType) vars.account_type = String(cfg.accountType);
  }

  // Site-kept memory of this visitor (see loadMemory); 'none' when we have nothing.
  const mem = loadMemory();
  if (!vars.user_name) vars.user_name = mem.name || '';
  vars.visitor_memory = formatMemory(mem) || 'none';

  if (process.env.NODE_ENV === 'development') {
    // Dev-only: this branch is stripped from the Netlify production build.
    console.log('🆔 ElevenLabs dynamic_variables →', vars);
  }
  return vars;
}

// Transcript text with any URLs made tappable (see utils/linkify). Links open in
// a new tab so tapping one doesn't end the chat or voice call in progress.
function renderMessageText(text, onLinkClick) {
  const parts = splitLinks(text);
  if (!parts.some((p) => p.type === 'link')) return text;
  return parts.map((p, i) => (p.type === 'link'
    ? <a key={i} href={p.href} target="_blank" rel={p.internal ? 'noopener' : 'noopener noreferrer'} onClick={() => onLinkClick && onLinkClick(p)}>{p.label}</a>
    : <React.Fragment key={i}>{p.value}</React.Fragment>));
}

function VoiceAgent() {
  const agentId = process.env.REACT_APP_ELEVENLABS_AGENT_ID;
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [selectedLanguage, setSelectedLanguage] = useState(LANGUAGES[0]); // Default to English
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [navigationLink, setNavigationLink] = useState(null); // Store navigation link
  const [isInstagramBrowser, setIsInstagramBrowser] = useState(false); // Detect Instagram browser
  const [showInstagramWarning, setShowInstagramWarning] = useState(false); // Show warning modal
  const [articles, setArticles] = useState([]); // Store all articles from sitemap
  const [showPulse, setShowPulse] = useState(true); // Show pulsing animation
  const [bubblePrompt, setBubblePrompt] = useState(() => getRandomQuestions(1)[0]); // Closed-state prompt, rotates until opened
  const [showWaQr, setShowWaQr] = useState(false); // Desktop: show the WhatsApp QR panel inside the chat
  const [waQrUrl, setWaQrUrl] = useState(null); // Generated QR data URL
  const [showExampleQuestions, setShowExampleQuestions] = useState(true); // Show example questions
  const [lastError, setLastError] = useState(null); // Store last error message
  const [isGmailBrowser, setIsGmailBrowser] = useState(false); // Detect Gmail browser
  const [showGmailWarning, setShowGmailWarning] = useState(false); // Show Gmail warning modal
  const [showGmailBanner, setShowGmailBanner] = useState(false); // Show Gmail browser banner on page load
  const [chatMode, setChatMode] = useState('text'); // 'text' (default: type first) or 'voice' (explicit choice)
  const [messages, setMessages] = useState([]); // Transcript shown in the panel [{id, role: 'user'|'ai', text}]
  const [isThinking, setIsThinking] = useState(false); // Waiting for Lenny's reply
  const messagesEndRef = useRef(null);
  const pendingMessageRef = useRef(null); // Message to send once the session connects
  const memorySessionRef = useRef(null); // Current session's memory record
  const entryRef = useRef('launcher'); // Where the visitor opened the chat from (GA4 `entry`)
  const askedRef = useRef(false); // Has a question been sent since the panel opened?
  const igVoiceOkRef = useRef(false); // Instagram browser: visitor chose "Try anyway" for voice
  const skipGreetingRef = useRef(false); // A typed question started this session: hide the agent's opening greeting
  const [textInput, setTextInput] = useState(''); // Text input for chat mode
  
  // Anchor positioning state
  const [isAnchored, setIsAnchored] = useState(false);
  const [anchorElement, setAnchorElement] = useState(null);
  const [anchorPosition, setAnchorPosition] = useState({ top: 0, left: 0, visible: false, measured: false });
  const [anchorConfig, setAnchorConfig] = useState({ offset: { x: 0, y: 0 }, position: 'left' });
  const containerRef = useRef(null);
  
  // Debug: Log when navigationLink changes
  useEffect(() => {
    console.log('🔄 navigationLink state changed:', navigationLink);
  }, [navigationLink]);

  // Check for anchor configuration
  useEffect(() => {
    const config = window.FoodFactsVoiceAgent || {};
    
    if (config.anchorTo) {
      const anchor = document.querySelector(config.anchorTo);
      
      if (anchor) {
        console.log('🎯 Anchor element found:', config.anchorTo);
        setIsAnchored(true);
        setAnchorElement(anchor);
        setAnchorConfig({
          offset: config.offset || { x: 0, y: 0 },
          position: config.position || 'left'
        });
      } else {
        console.log('⚠️ Anchor element not found:', config.anchorTo);
      }
    }
  }, []);

  // Update position based on anchor element's position in viewport
  const updateAnchorPosition = useCallback(() => {
    if (!anchorElement) return;
    
    const rect = anchorElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    
    // Check if anchor is visible in viewport - more strict check
    // Element must have at least 50px visible in viewport
    const minVisiblePx = 50;
    const isVisible = rect.bottom > minVisiblePx && 
                      rect.top < (viewportHeight - minVisiblePx) &&
                      rect.right > 0 && 
                      rect.left < viewportWidth;
    
    // Calculate position relative to viewport (fixed positioning)
    const top = rect.top + (rect.height / 2) + (anchorConfig.offset.y || 0);
    let left;
    
    if (anchorConfig.position === 'left') {
      left = rect.left + (anchorConfig.offset.x || 0);
    } else {
      left = rect.right + (anchorConfig.offset.x || 0);
    }
    
    setAnchorPosition(prev => {
      // Only update if values actually changed to prevent unnecessary re-renders
      if (prev.top !== top || prev.left !== left || prev.visible !== isVisible || !prev.measured) {
        return { top, left, visible: isVisible, measured: true };
      }
      return prev;
    });
  }, [anchorElement, anchorConfig]);

  // Set up scroll and resize listeners for anchor tracking
  useEffect(() => {
    if (!isAnchored || !anchorElement) return;
    
    // Initial position update
    updateAnchorPosition();
    
    // Update on scroll and resize - use both window and document for better compatibility
    const handleScroll = () => {
      requestAnimationFrame(updateAnchorPosition);
    };
    
    // Listen on window, document, and document.documentElement for maximum compatibility
    window.addEventListener('scroll', handleScroll, { passive: true, capture: true });
    document.addEventListener('scroll', handleScroll, { passive: true, capture: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    
    // Also use an interval as a fallback to ensure position updates
    const intervalId = setInterval(updateAnchorPosition, 100);
    
    // Also observe for DOM changes that might affect position
    const observer = new MutationObserver(handleScroll);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll, { capture: true });
      document.removeEventListener('scroll', handleScroll, { capture: true });
      window.removeEventListener('resize', handleScroll);
      clearInterval(intervalId);
      observer.disconnect();
    };
  }, [isAnchored, anchorElement, updateAnchorPosition]);

  // Detect Instagram and Gmail browsers on mount
  useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isInstagram = /Instagram/i.test(userAgent);
    const isGmail = /Gmail|Googlemail|Google-Inbox|gsa/i.test(userAgent);
    setIsInstagramBrowser(isInstagram);
    setIsGmailBrowser(isGmail);
    if (isInstagram) {
      console.log('⚠️ Instagram in-app browser detected - voice features may not work');
    }
    if (isGmail) {
      // Typed chat works here; only voice needs a real browser, so the warning waits until
      // someone chooses voice (see startVoice) instead of a banner on every page view.
      console.log('⚠️ Gmail in-app browser detected - voice features not supported');
    }
  }, []);

  // Fetch articles from sitemap on mount
  useEffect(() => {
    const loadArticles = async () => {
      const fetchedArticles = await fetchArticles();
      setArticles(fetchedArticles);
    };
    loadArticles();
  }, []);

  // Rotate the closed-state prompt through the suggested questions: a fresh one on every
  // page load, then a new one every 7 seconds until the chat is opened.
  useEffect(() => {
    if (isChatOpen) return undefined;
    const timer = setInterval(() => {
      setBubblePrompt((prev) => {
        let next = getRandomQuestions(1)[0];
        if (next === prev) next = getRandomQuestions(1)[0];
        return next;
      });
    }, 7000);
    return () => clearInterval(timer);
  }, [isChatOpen]);

  // Generate the WhatsApp QR code the first time the desktop panel is shown.
  useEffect(() => {
    if (!showWaQr || waQrUrl) return undefined;
    let cancelled = false;
    QRCode.toDataURL(getWhatsAppLink(), { width: 220, margin: 1, color: { dark: '#1a3d2e', light: '#ffffff' } })
      .then((url) => { if (!cancelled) setWaQrUrl(url); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [showWaQr, waQrUrl]);

  // Host-page hooks on window.FoodFactsVoiceWidget, for site scripts (ff-members.js):
  //   openWhatsApp()          the WhatsApp panel (QR on desktop) without starting a session
  //   ask(question, {entry})  open the chat and send the question (article cards, verdict link, search)
  //   open({entry})           open the chat ready to type
  // They are only ever called from a visitor's tap on the page; the widget never opens by itself.
  // Calls made before this bundle arrived were queued by the lazy loader in `_queue`.
  // The window functions go through a ref so they always see the current state.
  const hostApiRef = useRef({});
  useEffect(() => {
    const w = (window.FoodFactsVoiceWidget = window.FoodFactsVoiceWidget || {});
    w.openWhatsApp = () => { setShowWaQr(true); setIsChatOpen(true); };
    w.ask = (question, opts) => hostApiRef.current.ask(question, opts);
    w.open = (opts) => hostApiRef.current.open(opts);
    const queued = Array.isArray(w._queue) ? w._queue.splice(0) : [];
    queued.forEach((item) => {
      if (item && item.type === 'ask') w.ask(item.text, item.opts);
      else if (item) w.open(item.opts);
    });
    return () => { delete w.openWhatsApp; delete w.ask; delete w.open; };
  }, []);

  // Keep the newest message in view.
  useEffect(() => {
    if (messagesEndRef.current && messagesEndRef.current.scrollIntoView) messagesEndRef.current.scrollIntoView({ block: 'nearest' });
  }, [messages, isThinking]);

  // Closing the chat hides the QR panel again.
  useEffect(() => {
    if (!isChatOpen) setShowWaQr(false);
  }, [isChatOpen]);

  // Never auto-open the widget. Just stop the pulsing hint after 10 seconds.
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowPulse(false);
    }, 10000);
    return () => clearTimeout(timer);
  }, []);

  // Mark as visited when user opens chat
  useEffect(() => {
    if (isChatOpen) {
      localStorage.setItem('foodfacts_voice_visited', 'true');
      setShowPulse(false); // Stop pulsing once opened
    }
  }, [isChatOpen]);



  // Get translations for current language
  const t = translations[selectedLanguage.code] || translations.en;
  // English shows a rotating suggested question; other languages keep their translated prompt.
  const bubbleText = selectedLanguage.code === 'en' ? bubblePrompt : (t.speechBubble || 'Ask me any question!');

  // Define client tools for navigation
  const { sendUserMessage, ...conversation } = useConversation({
    clientTools: {
      get_page_content: () => {
        // Extract current page information
        const pageInfo = {
          url: window.location.href,
          title: document.title,
          pathname: window.location.pathname,
          // Extract main article content if available
          articleContent: document.querySelector('article')?.innerText?.substring(0, 2000) || '',
          metaDescription: document.querySelector('meta[name="description"]')?.content || '',
          h1: document.querySelector('h1')?.innerText || ''
        };
        
        console.log('Page info requested:', pageInfo);
        
        return JSON.stringify({
          message: "Current page information retrieved",
          data: pageInfo
        });
      },

      search_articles: (parameters) => {
        console.log('🔍 search_articles called with parameters:', parameters);
        const { query } = parameters;
        
        if (!query) {
          return JSON.stringify({
            message: "No search query provided",
            results: []
          });
        }
        
        // Search articles
        const matches = searchArticles(query, articles);
        console.log(`📚 Found ${matches.length} articles matching "${query}"`);
        
        if (matches.length === 0) {
          return JSON.stringify({
            message: `No articles found matching "${query}"`,
            results: []
          });
        }
        
        // Return top matches with URLs
        const results = matches.map(article => ({
          title: article.title,
          url: article.url,
          slug: article.slug
        }));
        
        return JSON.stringify({
          message: `Found ${matches.length} articles matching "${query}"`,
          results,
          topMatch: results[0]
        });
      },
      
      redirectToExternalURL: (parameters) => {
        console.log('🔧 redirectToExternalURL called with parameters:', parameters);
        const { url } = parameters;
        console.log('🔍 Navigation requested for:', url);
        
        // Check if it's a full article URL from search_articles
        if (url.includes('foodfacts.org/articles/')) {
          console.log('📝 Direct article URL detected');
          
          // Extract article title from URL
          const slug = url.split('/articles/')[1];
          const title = slug
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
          
          // Store the navigation link to display as a button
          const linkData = {
            url: url,
            name: title
          };
          console.log('💾 Setting article navigation link:', linkData);
          setNavigationLink(linkData);
          
          const responseMessage = `I found that article for you! Click the button below to read "${title}".`;
          console.log('📤 Returning message:', responseMessage);
          return responseMessage;
        }
        
        console.log('📋 Available sections:', Object.keys(sectionMappings));
        
        // Extract section name from URL if full URL is provided
        let sectionName = url;
        if (url.includes('foodfacts.org')) {
          // Extract path from full URL (e.g., "https://www.foodfacts.org/the-climate-crisis" -> "the-climate-crisis")
          const urlObj = new URL(url);
          sectionName = urlObj.pathname.replace(/^\//, ''); // Remove leading slash
          console.log('🔗 Extracted section from URL:', sectionName);
        }
        
        // Find the matching section (case-insensitive)
        // Try exact match first, then check if any section path matches
        let sectionKey = Object.keys(sectionMappings).find(
          key => key.toLowerCase() === sectionName.toLowerCase()
        );
        
        // If not found, try matching by path
        if (!sectionKey) {
          sectionKey = Object.keys(sectionMappings).find(
            key => sectionMappings[key].path.toLowerCase() === `/${sectionName.toLowerCase()}`
          );
          console.log('🔍 Tried path matching, found:', sectionKey);
        }
        
        console.log('🔑 Found section key:', sectionKey);
        
        if (sectionKey) {
          const targetSection = sectionMappings[sectionKey];
          const targetUrl = `https://www.foodfacts.org${targetSection.path}`;
          
          // Store the navigation link to display as a button
          const linkData = {
            url: targetUrl,
            name: targetSection.name
          };
          console.log('💾 Setting navigation link:', linkData);
          setNavigationLink(linkData);
          
          const responseMessage = `I found the ${targetSection.name} section for you! Click the button below to visit it.`;
          console.log('📤 Returning message:', responseMessage);
          return responseMessage;
        }
        
        // If no section found, try to use the URL directly
        if (url.startsWith('http')) {
          const linkData = {
            url: url,
            name: 'Requested Page'
          };
          console.log('💾 Setting direct URL navigation link:', linkData);
          setNavigationLink(linkData);
          return `I found a link for you! Click the button below to visit it.`;
        }
        
        console.log('❌ Section not found:', sectionName);
        return `I couldn't find a section matching "${sectionName}". Try asking about specific topics like "health", "climate crisis", or "nutrition".`;
      }
    },
    onConnect: () => {
      console.log('✅ Connected to ElevenLabs');
      setConnectionStatus('connected');
      if (!memorySessionRef.current) memorySessionRef.current = { at: new Date().toISOString(), lines: [] };
      if (pendingMessageRef.current) {
        const q = pendingMessageRef.current;
        pendingMessageRef.current = null;
        setTimeout(() => sendUserMessage(q), 300);
      }
    },
    onDisconnect: () => {
      console.log('🔌 Disconnected from ElevenLabs');
      setConnectionStatus('disconnected');
      memorySessionRef.current = null;
    },
    onError: (error) => {
      console.error('❌ ElevenLabs error:', error);
      setConnectionStatus('error');
      setIsThinking(false);
      pendingMessageRef.current = null;
      const errorMessage = error?.message || error?.toString() || 'Unknown error';
      console.error('Error details:', errorMessage);
      setLastError(errorMessage);
      if (errorMessage.includes('NotAllowedError') || errorMessage.includes('NotFoundError') || errorMessage.includes('CORS')) {
        console.error('Microphone, permission, or CORS issue detected');
      }
    },
    onMessage: (message) => {
      console.log('📩 Message received:', message);
      const source = message && (message.source || message.role);
      const role = source === 'user' ? 'user' : 'ai';
      const raw = message && (message.message || message.text);
      // Voice delivery cues like [Warmly] are for the voice, not the transcript or the memory notes.
      const text = role === 'ai' ? stripVoiceCues(raw) : raw;
      if (!text) return;
      // When a typed question opened the session, the agent's scripted greeting arrives between the
      // question and the answer; the panel already greets, so drop it and keep waiting for the answer.
      if (role === 'ai' && skipGreetingRef.current) {
        skipGreetingRef.current = false;
        if (/lenny|लेनी/i.test(text.slice(0, 120))) return;
      }
      recordMemoryLine(memorySessionRef, role, text);
      setMessages((prev) => {
        // The SDK echoes our own typed message back; skip it if it's already the last user bubble.
        if (role === 'user' && prev.length && prev[prev.length - 1].role === 'user' && prev[prev.length - 1].text === text) return prev;
        return [...prev, { id: Date.now() + Math.random(), role, text }];
      });
      if (role === 'ai') setIsThinking(false);
    }
  });

  const { isSpeaking } = conversation;

  // Get user-friendly error message based on error type
  const getErrorMessage = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isGmail = /Gmail|Googlemail|Google-Inbox|gsa/i.test(userAgent);
    
    if (isGmail) {
      return t.error.gmailBrowser;
    }
    
    if (lastError?.includes('NotAllowedError') || lastError?.includes('Permission')) {
      return t.error.microphoneError;
    }
    
    if (lastError?.includes('NotFoundError') || lastError?.includes('NotSupportedError')) {
      return t.error.browserNotSupported;
    }
    
    return t.error.failed;
  };

  // Start conversation with language setting
  const startConversation = async (modeOverride) => {
    const mode = modeOverride || chatMode;
    try {
      setConnectionStatus('connecting');
      console.log(`🎤 Starting conversation in ${selectedLanguage.name}...`);
      
      // Check for Gmail browser restrictions
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isGmail = /Gmail|Googlemail|Google-Inbox|gsa/i.test(userAgent);
      
      if (isGmail) {
        console.error('❌ Gmail browser detected - voice features not supported');
        setLastError('Gmail browser detected');
        setConnectionStatus('error');
        return;
      }
      
      // Voice needs the microphone; text-only sessions don't ask for it.
      if (mode === 'voice') {
        await navigator.mediaDevices.getUserMedia({ audio: true });
      }
      
      // Start the conversation with the selected language.
      // dynamicVariables (incl. a stable user_id) are read by ElevenLabs at
      // conversation initiation; merged so any future vars are preserved.
      await conversation.startSession({
        agentId: agentId,
        connectionType: 'websocket',
        textOnly: mode === 'text',
        // Session-level user id: this is what ElevenLabs' Users page and its user memory key on
        // (the user_id dynamic variable below is only prompt text).
        userId: getOrCreateUserId(),
        dynamicVariables: buildDynamicVariables(),
        overrides: {
          agent: {
            language: selectedLanguage.code
          }
        }
      });
      
      console.log('✅ Conversation started successfully');
    } catch (error) {
      console.error('❌ Failed to start conversation:', error);
      const errorMessage = error?.message || error?.toString() || 'Unknown error';
      console.error('Error details:', errorMessage);
      setLastError(errorMessage);
      setConnectionStatus('error');
    }
  };

  // Stop conversation
  const stopConversation = async () => {
    try {
      await conversation.endSession();
      setConnectionStatus('disconnected');
      console.log('🛑 Conversation stopped');
    } catch (error) {
      console.error('❌ Failed to stop conversation:', error);
    }
  };

  // Ask Lenny something (typed or a suggestion chip). Starts a session on first use.
  const askQuestion = async (text) => {
    const q = (text || '').trim();
    if (!q) return;
    track('lenny_question', { entry: entryRef.current, first: askedRef.current ? 'no' : 'yes' });
    askedRef.current = true;
    setMessages((prev) => [...prev, { id: Date.now() + Math.random(), role: 'user', text: q }]);
    recordMemoryLine(memorySessionRef, 'user', q);
    setIsThinking(true);
    try {
      if (connectionStatus === 'connected') {
        sendUserMessage(q);
      } else {
        pendingMessageRef.current = q;
        skipGreetingRef.current = true;
        // A typed question or chip always starts a text chat: after a voice call this closure's
        // chatMode can still be 'voice' (e.g. a host chip reopening the panel), which would ask for the mic.
        setChatMode('text');
        await startConversation('text');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      setIsThinking(false);
    }
  };

  // Send text message
  const handleSendTextMessage = async () => {
    if (!textInput.trim()) return;
    const q = textInput;
    setTextInput('');
    await askQuestion(q);
  };

  // Voice is an explicit choice: end any text-only session and start a voice one (asks for the mic).
  // In-app browsers get their warning here, not when the chat opens: typing works fine in them.
  const startVoice = async () => {
    if (isGmailBrowser) { setShowGmailWarning(true); return; }
    if (isInstagramBrowser && !igVoiceOkRef.current) { setShowInstagramWarning(true); return; }
    track('lenny_voice', { entry: entryRef.current });
    skipGreetingRef.current = false; // a call opens with Lenny's spoken greeting
    if (connectionStatus === 'connected' || connectionStatus === 'connecting') {
      try { await conversation.endSession(); } catch (e) { /* ignore */ }
      setConnectionStatus('disconnected');
    }
    setChatMode('voice');
    await startConversation('voice');
  };

  // Back to typing: end the voice session; the next message starts a text-only one.
  const switchToText = async () => {
    if (connectionStatus === 'connected' || connectionStatus === 'connecting') {
      try { await conversation.endSession(); } catch (e) { /* ignore */ }
      setConnectionStatus('disconnected');
    }
    setChatMode('text');
  };

  // Handle Enter key in text input
  const handleTextInputKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendTextMessage();
    }
  };

  // Open the panel, type-first; voice stays an explicit choice inside it.
  const openPanel = (entry) => {
    entryRef.current = entry || 'launcher';
    askedRef.current = false;
    setShowWaQr(false);
    setChatMode('text');
    setIsChatOpen(true);
    track('lenny_open', { entry: entryRef.current });
  };

  // Desktop only: put the cursor in the composer (phones would pop the keyboard over the answer).
  const focusComposer = () => {
    if (isMobileDevice()) return;
    setTimeout(() => {
      const el = document.querySelector('.text-input-field');
      if (el) { try { el.focus({ preventScroll: true }); } catch (e) { /* ignore */ } }
    }, 80);
  };

  // A tap on a host-page entry point (article card chip, verdict link, search suggestion).
  const openFromHost = (text, opts) => {
    const entry = (opts && opts.entry) || 'host';
    if (!isChatOpen) openPanel(entry);
    else entryRef.current = entry;
    const q = (text || '').trim();
    if (q) askQuestion(q);
    else focusComposer();
  };
  hostApiRef.current = {
    ask: (question, opts) => openFromHost(question, opts),
    open: (opts) => openFromHost('', opts),
  };

  // Toggle chat open/close. Launchers pass their entry name; other callers pass a click event.
  const toggleChat = (entry) => {
    if (!isChatOpen) {
      openPanel(typeof entry === 'string' ? entry : 'launcher');
      focusComposer();
    } else {
      setIsChatOpen(false);
      // Stop conversation when closing
      if (connectionStatus === 'connected') {
        stopConversation();
      }
    }
  };

  // Handle language change
  const handleLanguageChange = async (lang) => {
    setSelectedLanguage(lang);
    setIsLanguageDropdownOpen(false);
    
    // If connected, restart with new language
    if (connectionStatus === 'connected') {
      await stopConversation();
      setTimeout(() => {
        startConversation();
      }, 500);
    }
  };

  // Get status indicator
  const getStatusIndicator = () => {
    switch (connectionStatus) {
      case 'connected':
        return { color: '🟢', text: isSpeaking ? t.status.speaking : t.status.connected };
      case 'connecting':
        return { color: '🟡', text: t.status.connecting };
      case 'error':
        return { color: '🔴', text: t.status.error };
      default:
        return { color: '⚪', text: t.status.disconnected };
    }
  };

  const status_indicator = getStatusIndicator();

  // WhatsApp entry point: phones jump straight into WhatsApp, desktops get a QR code in the chat panel.
  const openWhatsApp = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    track('lenny_whatsapp', { entry: entryRef.current });
    if (isMobileDevice()) return; // let the wa.me link open natively
    if (e && e.preventDefault) e.preventDefault();
    setShowWaQr(true);
    // Open the panel directly (toggleChat would auto-start a voice session and ask for the mic).
    if (!isChatOpen) setIsChatOpen(true);
  };

  const renderWhatsAppQr = () => (
    <div className="whatsapp-qr">
      <div className="whatsapp-qr-title">Scan to chat with Lenny on WhatsApp</div>
      {waQrUrl ? (
        <img className="whatsapp-qr-img" src={waQrUrl} alt="QR code that opens a WhatsApp chat with Lenny" />
      ) : (
        <div className="whatsapp-qr-loading">Loading…</div>
      )}
      <a className="whatsapp-qr-link" href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer">Or open WhatsApp on this device →</a>
      <div className="whatsapp-qr-number">{formatWhatsAppNumber()} · text or send a voice note</div>
      <button type="button" className="switch-mode-button" onClick={() => setShowWaQr(false)}>Back to chat</button>
    </div>
  );

  // Render speech bubble trigger for anchored mode (the homepage, next to the lime mascot).
  // It says who Lenny is and what to do; the rotating example question sits underneath.
  const renderSpeechBubbleTrigger = () => (
    <div
      className="speech-bubble-trigger"
      role="button"
      tabIndex={0}
      aria-label="Ask Lenny, the foodfacts.org AI fact-checker, any food question"
      onClick={() => toggleChat('home_bubble')}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleChat('home_bubble'); } }}
      // Placed with the `translate` property, not top/left: Chrome counts every top/left move of a fixed element as a
      // layout shift (CLS), and this one follows the mascot on every scroll frame. translate moves are not counted.
      // The site's own `transform` offsets (home-v3.css) still compose on top of it.
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        translate: `${anchorPosition.left}px ${anchorPosition.top}px`,
        transform: 'translateY(-50%)',
        opacity: anchorPosition.visible ? 1 : 0,
        pointerEvents: anchorPosition.visible ? 'auto' : 'none',
        transition: 'opacity 0.2s ease-out',
        zIndex: 10000
      }}
    >
      <div className={`speech-bubble ${showPulse ? 'pulse' : ''}`}>
        {selectedLanguage.code === 'en' ? (
          <>
            <span className="speech-bubble-kicker">Lenny · AI fact-checker</span>
            <span className="speech-bubble-text">Ask me any food question <span aria-hidden="true">→</span></span>
            <span className="speech-bubble-example">Try: “{bubblePrompt}”</span>
          </>
        ) : (
          <span className="speech-bubble-text">{bubbleText}</span>
        )}
        <div className="speech-bubble-tail"></div>
      </div>
    </div>
  );

  // The launcher: Lenny's face and "Ask Lenny", on phones as well as desktop ("a food question"
  // is added on wider screens). The language picker and "Powered by" live inside the panel.
  const renderLauncher = (entry) => (
    <button
      type="button"
      className={`lenny-launcher ${showPulse ? 'pulse' : ''}`}
      onClick={() => toggleChat(entry)}
      aria-label="Ask Lenny, the foodfacts.org AI fact-checker"
    >
      <img className="lenny-launcher-avatar" src={LENNY_AVATAR} alt="" width="40" height="40" />
      <span className="lenny-launcher-label">
        {selectedLanguage.code === 'en' ? (
          <>Ask Lenny<span className="lenny-launcher-more"> a food question</span></>
        ) : (t.buttonLabel || t.title)}
      </span>
    </button>
  );

  // Render the chat bubble (used in both modes)
  const renderChatBubble = () => (
    <div className={`chat-bubble ${isAnchored ? 'anchored-chat' : ''}`}>
      <div className="chat-bubble-header">
        <div className="chat-bubble-title">
          <div className="chat-bubble-avatar">{chatMode === 'voice' ? '🎤' : '💬'}</div>
          <div>
            <div className="chat-bubble-name">{t.title}</div>
            <div className="chat-bubble-status">{status_indicator.color} {status_indicator.text}</div>
          </div>
        </div>
        <button className="chat-bubble-close" onClick={toggleChat}>×</button>
      </div>
      
      <div className="chat-bubble-content">
        {messages.length === 0 && (
          <div className="chat-bubble-welcome">
            <p className="chat-bubble-welcome-text">{t.welcome} 👋</p>
            <p className="chat-bubble-subtitle">{t.subtitle}</p>
          </div>
        )}

        {messages.length > 0 && (
          <div className="chat-messages" role="log" aria-live="polite">
            {messages.map((m) => (
              <div key={m.id} className={`chat-message chat-message-${m.role}`}>{renderMessageText(m.text, (p) => track('lenny_link_click', { entry: entryRef.current, link_type: p.internal ? 'site' : 'external' }))}</div>
            ))}
            {isThinking && <div className="chat-message chat-message-ai chat-message-thinking">Lenny is thinking…</div>}
            <div ref={messagesEndRef} />
          </div>
        )}

        {showWaQr && renderWhatsAppQr()}

        {chatMode === 'voice' && (
          <div className="voice-mode-panel">
            <div className="voice-mode-status">
              {connectionStatus === 'connected'
                ? (isSpeaking ? '🔊 Lenny is speaking…' : '🎤 Listening, just talk')
                : connectionStatus === 'connecting' ? '⏳ Connecting…' : '🎤 Voice call ended'}
            </div>
            <button type="button" className="switch-mode-button" onClick={switchToText}>⌨️ Type instead</button>
          </div>
        )}

        {chatMode === 'text' && (
          <div className="text-input-container">
            <input
              type="text"
              className="text-input-field"
              placeholder="Ask Lenny a question…"
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              onKeyPress={handleTextInputKeyPress}
              disabled={connectionStatus === 'connecting'}
            />
            <button
              className="text-input-send-button"
              onClick={handleSendTextMessage}
              disabled={!textInput.trim() || connectionStatus === 'connecting'}
              title="Send message (Enter)"
            >
              📤
            </button>
          </div>
        )}

        {chatMode === 'text' && (
          <div className="chat-secondary-row">
            <button type="button" className="voice-switch-button" onClick={startVoice}>🎤 Talk to Lenny by voice</button>
            <a className="wa-switch-button" href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer" onClick={openWhatsApp} title="Message Lenny on WhatsApp">
              <WhatsAppGlyph /> WhatsApp
            </a>
          </div>
        )}

        {messages.length === 0 && (() => {
          const randomQuestions = getRandomQuestions(3);
          return (
            <div className="example-questions">
              <div className="example-questions-title">Or try one of these:</div>
              <div className="example-questions-list">
                {randomQuestions.map((question, index) => (
                  <button key={index} className="example-question-button" onClick={() => askQuestion(question)}>
                    {question}
                  </button>
                ))}
              </div>
            </div>
          );
        })()}

        {connectionStatus === 'error' && (
          <div className="chat-bubble-error">
            <p>{t.status.error}</p>
            <p className="error-details">
              {getErrorMessage()}
            </p>
            <button onClick={startConversation} className="retry-button">
              {t.retry}
            </button>
          </div>
        )}
        
        {navigationLink && (
          <div className="navigation-link-container">
            <a 
              href={navigationLink.url} 
              className="navigation-button"
              target="_self"
              onClick={() => { track('lenny_link_click', { entry: entryRef.current, link_type: 'visit_button' }); setNavigationLink(null); }}
            >
              📄 Visit {navigationLink.name}
            </a>
          </div>
        )}
      </div>
      
      <div className="chat-bubble-footer">
        <div className="chat-bubble-footer-content">
          <span>💡 {chatMode === 'voice' ? t.hint : (t.textHint || 'Type your question, or tap "Talk to Lenny by voice".')}</span>
          {isAnchored && (
            <div className="language-selector-inline">
              <button 
                className="language-button-small"
                onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                aria-label="Select language"
              >
                <span>{selectedLanguage.flag}</span>
                <span>▼</span>
              </button>
              
              {isLanguageDropdownOpen && (
                <div className="language-dropdown-inline">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      className={`language-option ${selectedLanguage.code === lang.code ? 'selected' : ''}`}
                      onClick={() => handleLanguageChange(lang)}
                    >
                      <span className="language-option-flag">{lang.flag}</span>
                      <span className="language-option-name">{lang.name}</span>
                      {selectedLanguage.code === lang.code && <span className="language-checkmark">✓</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        {isAnchored && (
          <div className="powered-by-inline">
            <a href="https://elevenlabs.io/conversational-ai" target="_blank" rel="noopener noreferrer">
              Powered by ElevenLabs Agents
            </a>
          </div>
        )}
      </div>
    </div>
  );

  // Collapsed state for non-anchored pages: just the labelled launcher.
  const renderDefaultButtons = () => (
    <div className="widget-buttons">
      {renderLauncher('launcher')}
    </div>
  );

  // If anchored mode, render the speech bubble with scroll tracking
  if (isAnchored) {
    return (
      <>
        {/* Gmail Browser Banner - Show proactively */}
        {showGmailBanner && (
          <div className="gmail-banner">
            <div className="gmail-banner-content">
              <span className="gmail-banner-icon">📱</span>
              <div className="gmail-banner-text">
                <p className="gmail-banner-title">Open in Your Browser</p>
                <p className="gmail-banner-instructions">Tap the three dots (⋯) at the bottom right, then select "Open in Chrome" or "Open in Safari" for the best experience.</p>
              </div>
              <button className="gmail-banner-close" onClick={() => setShowGmailBanner(false)}>×</button>
            </div>
          </div>
        )}
        
        {/* Speech bubble that follows the anchor */}
        {!isChatOpen && renderSpeechBubbleTrigger()}

        {/* Once the anchor (the homepage mascot) is off screen, keep Lenny reachable in the corner */}
        {!isChatOpen && anchorPosition.measured && !anchorPosition.visible && (
          <div className="voice-agent-container lenny-home-float">
            <div className="widget-buttons">{renderLauncher('home_float')}</div>
          </div>
        )}
        
        {/* Chat bubble - fixed position when open */}
        {isChatOpen && (
          <div className="anchored-chat-wrapper">
            {renderChatBubble()}
          </div>
        )}
        
        {/* Gmail Browser Warning Modal */}
        {showGmailWarning && (
          <div className="instagram-warning-overlay" onClick={() => setShowGmailWarning(false)}>
            <div className="instagram-warning-modal" onClick={(e) => e.stopPropagation()}>
              <div className="instagram-warning-header">
                <h3>⚠️ Voice Features Not Available</h3>
                <button className="instagram-warning-close" onClick={() => setShowGmailWarning(false)}>×</button>
              </div>
              <div className="instagram-warning-content">
                <p>Voice features are not supported in Gmail's in-app browser.</p>
                <p><strong>For the best experience, please open this page in your default browser:</strong></p>
                <ol>
                  <li>Copy the link from Gmail</li>
                  <li>Open it in Chrome, Safari, or Firefox</li>
                </ol>
              </div>
              <div className="instagram-warning-buttons">
                <button 
                  className="instagram-warning-button instagram-warning-button-secondary"
                  onClick={() => setShowGmailWarning(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Instagram Browser Warning Modal */}
        {showInstagramWarning && (
          <div className="instagram-warning-overlay" onClick={() => setShowInstagramWarning(false)}>
            <div className="instagram-warning-modal" onClick={(e) => e.stopPropagation()}>
              <div className="instagram-warning-header">
                <h3>⚠️ Voice Features Limited</h3>
                <button className="instagram-warning-close" onClick={() => setShowInstagramWarning(false)}>×</button>
              </div>
              <div className="instagram-warning-content">
                <p>Instagram's browser doesn't fully support voice features.</p>
                <p><strong>For the best experience, please open this page in your default browser:</strong></p>
                <ol>
                  <li>Tap the <strong>three dots (•••)</strong> in the top right</li>
                  <li>Select <strong>"Open in Browser"</strong> or <strong>"Open in Safari/Chrome"</strong></li>
                </ol>
              </div>
              <div className="instagram-warning-buttons">
                <button 
                  className="instagram-warning-button instagram-warning-button-primary"
                  onClick={() => { setShowInstagramWarning(false); igVoiceOkRef.current = true; startVoice(); }}
                >
                  Try Anyway
                </button>
                <button 
                  className="instagram-warning-button instagram-warning-button-secondary"
                  onClick={() => setShowInstagramWarning(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Non-anchored mode - render normally
  return (
    <div 
      ref={containerRef}
      className="voice-agent-container"
    >
      {/* Gmail Browser Banner - Show proactively */}
      {showGmailBanner && (
        <div className="gmail-banner">
          <div className="gmail-banner-content">
            <span className="gmail-banner-icon">📱</span>
            <div className="gmail-banner-text">
              <p className="gmail-banner-title">Open in Your Browser</p>
              <p className="gmail-banner-instructions">Tap the three dots (⋯) at the bottom right, then select "Open in Chrome" or "Open in Safari" for the best experience.</p>
            </div>
            <button className="gmail-banner-close" onClick={() => setShowGmailBanner(false)}>×</button>
          </div>
        </div>
      )}
      
      {/* Chat Bubble - appears when open */}
      {isChatOpen && renderChatBubble()}

      {/* Default Widget Buttons (non-anchored mode) */}
      {!isChatOpen && renderDefaultButtons()}

      {/* Gmail Browser Warning Modal */}
      {showGmailWarning && (
        <div className="instagram-warning-overlay" onClick={() => setShowGmailWarning(false)}>
          <div className="instagram-warning-modal" onClick={(e) => e.stopPropagation()}>
            <div className="instagram-warning-header">
              <h3>⚠️ Voice Features Not Available</h3>
              <button className="instagram-warning-close" onClick={() => setShowGmailWarning(false)}>×</button>
            </div>
            <div className="instagram-warning-content">
              <p>Voice features are not supported in Gmail's in-app browser.</p>
              <p><strong>For the best experience, please open this page in your default browser:</strong></p>
              <ol>
                <li>Copy the link from Gmail</li>
                <li>Open it in Chrome, Safari, or Firefox</li>
              </ol>
            </div>
            <div className="instagram-warning-buttons">
              <button 
                className="instagram-warning-button instagram-warning-button-secondary"
                onClick={() => setShowGmailWarning(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Instagram Browser Warning Modal */}
      {showInstagramWarning && (
        <div className="instagram-warning-overlay" onClick={() => setShowInstagramWarning(false)}>
          <div className="instagram-warning-modal" onClick={(e) => e.stopPropagation()}>
            <div className="instagram-warning-header">
              <h3>⚠️ Voice Features Limited</h3>
              <button className="instagram-warning-close" onClick={() => setShowInstagramWarning(false)}>×</button>
            </div>
            <div className="instagram-warning-content">
              <p>Instagram's browser doesn't fully support voice features.</p>
              <p><strong>For the best experience, please open this page in your default browser:</strong></p>
              <ol>
                <li>Tap the <strong>three dots (•••)</strong> in the top right</li>
                <li>Select <strong>"Open in Browser"</strong> or <strong>"Open in Safari/Chrome"</strong></li>
              </ol>
            </div>
            <div className="instagram-warning-buttons">
              <button 
                className="instagram-warning-button instagram-warning-button-primary"
                onClick={() => { setShowInstagramWarning(false); igVoiceOkRef.current = true; startVoice(); }}
              >
                Try Anyway
              </button>
              <button 
                className="instagram-warning-button instagram-warning-button-secondary"
                onClick={() => setShowInstagramWarning(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VoiceAgent;
